import { ApiSignPostRequest, SignDTO } from "@api/client";
import { useAppDispatch } from "core/store/hooks";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import AppButton from "shared/design-system/components/app-button";
import AppDropzone from "shared/design-system/components/app-dropzone";
import AppInput from "shared/design-system/components/app-input";
import AppModal from "shared/design-system/components/app-modal";
import AppTextarea from "shared/design-system/components/app-textarea";
import { AppToastService } from "shared/design-system/components/app-toast/app-toast.service";
import * as Yup from "yup";
import { signCreate, signEdit, signGetDetails } from "../_redux/actions";
import "./style.scss";

interface Props {
	show: boolean;
	onHide: (reload?: boolean) => void;

	entId?: number | undefined;
	isLibrary?: boolean;
}

const SignEditModal: React.FC<Props> = (props) => {
	const { show, entId, onHide } = props;

	const dispatch = useAppDispatch();

	const [data, setData] = useState<SignDTO | undefined>();

	const handleHide = (reload?: boolean) => {
		setData(undefined);
		onHide(reload);
	};

	const getDetails = () => {
		if (!entId) return;

		dispatch(signGetDetails(entId))
			.unwrap()
			.then((res) => setData(res));
	};

	const handleSubmit = async (values: FormValues) => {
		let request;

		let _values: ApiSignPostRequest = {
			...(entId ? data : {}),
			title: values.title,
			description: values.description,
			url: data?.url || undefined,
			file: values.file,
		};

		if (values?.file) {
			_values.file = values?.file;
		}

		if (entId) {
			request = dispatch(signEdit({ ..._values, entId }));
		} else request = dispatch(signCreate(_values));

		request.unwrap().then((res) => {
			if (res) {
				AppToastService.success(
					`Segno ${entId ? "modificato" : "aggiunto"} con successo`
				);
				handleHide(true);
			}
		});
	};

	useEffect(() => {
		if (show) getDetails();
	}, [show]);

	if (!show || (entId && !data)) return null;

	return (
		<AppModal
			show={show}
			onClose={() => handleHide()}
			title={`${entId ? "Modifica" : "Nuovo"} Segno`}
			block
			className="sign-edit-modal"
		>
			<Formik<FormValues>
				initialValues={{
					title: data?.title || "",
					description: data?.description || "",
					file: undefined,
				}}
				validationSchema={Yup.object().shape({
					title: Yup.string().required(),
				})}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({
					values,
					errors,
					touched,
					handleSubmit: _handleSubmit,
					handleChange,
					handleBlur,
					setFieldValue,
				}) => {
					const handleAddFile = (file: File) => {
						setFieldValue("file", file);
					};

					const handleDeleteFile = () => {
						setFieldValue("file", undefined);
					};
					return (
						<>
							<form
								onSubmit={_handleSubmit}
								className="grid grid-cols-1 gap-3 justify-items-center"
								autoComplete="off"
							>
								{(data?.url || values.file) && (
									<video
										src={
											data?.url ||
											(values?.file
												? URL.createObjectURL(values.file)
												: undefined)
										}
										controls
										controlsList="nodownload"
										playsInline
										preload="metadata"
									>
										Il tuo browser non supporta il video.
									</video>
								)}

								<AppInput
									label="Titolo"
									name="title"
									value={values.title!}
									onChange={handleChange}
									onBlur={handleBlur}
									isInvalid={!!errors?.title && touched?.title}
									error={errors?.title}
									autoFocus
								/>

								<AppTextarea
									rows={3}
									label="Descrizione"
									name="description"
									value={values.description!}
									onChange={handleChange}
									onBlur={handleBlur}
									isInvalid={!!errors?.description && touched?.description}
									error={errors?.description}
									autoFocus
								/>

								<AppDropzone
									className="w-full"
									accept="video"
									label="Video"
									file={values?.file}
									onDrop={handleAddFile}
									onDelete={handleDeleteFile}
									isInvalid={!!errors?.file && touched?.file}
									error={errors?.file}
								/>

								<div className="grid grid-cols-1 md:grid-cols-2 form-actions  w-full">
									<div className="order-2 md:order-1">
										<AppButton
											color="white"
											onClick={() => handleHide()}
											className="w-full md:w-auto"
										>
											Annulla
										</AppButton>
									</div>

									<div className="text-end order-1 md:order-2">
										<AppButton type="submit" className="w-full md:w-auto">
											Salva
										</AppButton>
									</div>
								</div>
							</form>
						</>
					);
				}}
			</Formik>
		</AppModal>
	);
};
export default SignEditModal;

interface FormValues {
	title: string;
	description: string;
	id?: number;
	file?: File;
}
